import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import Layout, { StatusBadge } from '../components/Layout'
import { ProjectForm } from '../components/Forms'
import { Btn, Msg, inputCls, money, fmtDate } from '../components/ui'
import { PROJECT_STATUSES, clientLabel, type Client, type Project } from '../lib/db'

export default function Projets() {
  const [params, setParams] = useSearchParams()
  const [projects, setProjects] = useState<Project[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [status, setStatus] = useState('actifs')
  const [q, setQ] = useState('')
  const [form, setForm] = useState<Partial<Project> | null>(null)
  const [msg, setMsg] = useState('')

  async function load() {
    const [p, c] = await Promise.all([supabase.from('projects').select('*').order('created_at', { ascending: false }), supabase.from('clients').select('*').order('company').order('full_name')])
    const list = (p.data as any) || []; setProjects(list); setClients((c.data as any) || [])
    const edit = params.get('edit'); if (edit) setForm(list.find((x: Project) => x.id === edit) || null)
    if (params.get('new')) setForm({})
  }
  useEffect(() => { load() }, [])
  const cname = (id: string) => clientLabel(clients.find(c => c.id === id))
  const close = () => { setForm(null); setParams({}) }

  async function quickStatus(p: Project, s: string) {
    setProjects(l => l.map(x => x.id === p.id ? { ...x, status: s } : x))
    const { error } = await supabase.from('projects').update({ status: s }).eq('id', p.id)
    if (error) setMsg('Erreur : ' + error.message)
  }
  async function remove(p: Project) {
    if (!confirm(`Supprimer le projet « ${p.title} » ? Les factures et vidéos liées seront aussi supprimées.`)) return
    const { error } = await supabase.from('projects').delete().eq('id', p.id)
    if (error) { setMsg('Erreur : ' + error.message); return }
    setProjects(l => l.filter(x => x.id !== p.id))
  }

  const filtered = projects.filter(p => (status === 'tous' || (status === 'actifs' ? !['livré', 'annulé'].includes(p.status) : p.status === status))
    && (!q || [p.title, cname(p.client_id), p.description].some(x => (x || '').toLowerCase().includes(q.toLowerCase()))))

  return (
    <Layout title="Projets" actions={<Btn onClick={() => setForm({})}>+ Nouveau projet</Btn>}>
      <div className="flex flex-wrap gap-3 mb-4">
        <input className={inputCls + ' max-w-xs'} placeholder="Rechercher…" value={q} onChange={e => setQ(e.target.value)} />
        <select className={inputCls + ' w-auto'} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="actifs">Actifs</option><option value="tous">Tous</option>{PROJECT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <Msg msg={msg} />
      <div className="border border-white/10 rounded overflow-x-auto mt-2">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-white/5 text-acier text-[11px] uppercase tracking-widest">
            <tr><th className="text-left px-4 py-3">Projet</th><th className="text-left px-4 py-3">Client</th><th className="text-left px-4 py-3">Tournage</th><th className="text-left px-4 py-3">Livraison</th><th className="text-right px-4 py-3">Budget</th><th className="text-left px-4 py-3">Statut</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                <td className="px-4 py-3"><button onClick={() => setForm(p)} className="font-medium hover:text-orange text-left">{p.title}</button>{p.description && <div className="text-xs text-acier truncate max-w-xs">{p.description}</div>}</td>
                <td className="px-4 py-3"><a href={`/admin/clients/${p.client_id}`} className="text-acier hover:text-orange">{cname(p.client_id)}</a></td>
                <td className="px-4 py-3 text-acier">{fmtDate(p.shoot_date)}</td>
                <td className="px-4 py-3 text-acier">{fmtDate(p.delivery_date)}</td>
                <td className="px-4 py-3 text-right text-acier">{p.budget ? money(p.budget) : '—'}</td>
                <td className="px-4 py-3">
                  <select className={inputCls + ' w-auto py-1'} value={p.status} onChange={e => quickStatus(p, e.target.value)}>{PROJECT_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap text-xs uppercase tracking-widest">
                  <a href={`/admin/comptabilite?tab=factures&new=1&client=${p.client_id}&project=${p.id}`} className="text-orange hover:underline mr-3">Facturer</a>
                  <button onClick={() => remove(p)} className="text-acier hover:text-red-300">Suppr.</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-acier">Aucun projet.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-acier mt-3">Statuts : {PROJECT_STATUSES.map(s => <StatusBadge key={s} status={s} />).reduce((a: any[], b, i) => [...a, i ? ' ' : '', b], [])}</p>
      {form && <ProjectForm initial={form} clients={clients} onClose={close} onSaved={() => { close(); load() }} />}
    </Layout>
  )
}
